import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { putMethodApiCall, getMethodApiCall } from "src/services/api";
import moment from "moment";
// import CryptoJS from "crypto-js";


const UpdateProfileForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [userData, setUserData] = useState({});
  
  // Get registration ID from localStorage for API call
  const currentUser = JSON.parse(localStorage.getItem("loginedUser"));
  const registrationId = currentUser?.trainee?.registration_id || currentUser?.registration_id;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm();

  useEffect(() => {
    // Always fetch user data from database on component mount
    if (registrationId) {
      fetchUserData();
    } else {
      navigate("/Trainee-login");
    }
  }, [registrationId]);

  useEffect(() => {
    // Set form values when userData is loaded
    if (userData && Object.keys(userData).length > 0) {
      Object.keys(userData).forEach(key => {
        if (key === 'date_of_birth' && userData[key]) {
          try {
            const formattedDate = moment(userData[key]).format("YYYY-MM-DD");
            setValue(key, formattedDate);
          } catch (error) {
            console.error("Error formatting date:", error);
            setValue(key, "");
          }
        }
        // } else if (key === 'phone_number' && userData[key]) {
        //   const decryptedPhone = decryptData(userData[key]);
        //   setValue(key, decryptedPhone);
        // } else 
        else if (userData[key] !== null && userData[key] !== undefined) {
          setValue(key, userData[key]);
        } else {
          setValue(key, "");
        }
      });
      setDataLoading(false);
    }
  }, [userData, setValue]);

  // Add extra back-event handlers so users can navigate back using
  // keyboard (Backspace, Alt+Left / Meta+Left) and mouse back button (auxclick)
  useEffect(() => {
    const goBack = () => navigate(-1);

    const onKeyDown = (e) => {
      try {
        const active = document.activeElement;
        const tag = active?.tagName;
        const isEditable = active?.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';

        // If user is typing in an input/textarea/select or an editable element, don't navigate
        if (isEditable) return;

        // Backspace (when not focused on an input), Alt+ArrowLeft or Meta+ArrowLeft (browser back shortcuts)
        if (
          e.key === 'Backspace' ||
          (e.altKey && e.key === 'ArrowLeft') ||
          (e.metaKey && e.key === 'ArrowLeft') ||
          e.key === 'BrowserBack'
        ) {
          e.preventDefault();
          goBack();
        }
      } catch (err) {
        // swallow errors to avoid breaking the page
        console.error('Back key handler error', err);
      }
    };

    const onAuxClick = (e) => {
      // Some mice expose back/forward buttons as auxclick with non-standard button values
      // Typical non-standard values seen in some browsers: 3 (XButton1/back), 4 (XButton2/forward)
      const btn = typeof e.button !== 'undefined' ? e.button : e.which;
      if (btn === 3) {
        goBack();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('auxclick', onAuxClick);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('auxclick', onAuxClick);
    };
  }, [navigate]);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/Trainee-login");
        return;
      }


      // Fetch user data from the server using registration ID
      const response = await getMethodApiCall(`/v1/api/trainee/by-reg-id/${registrationId}`);

      if (response.status === 200) {
        const data = response.data;
        if (data.success && data.trainee) {
          setUserData(data.trainee);
        } else {
          setDataLoading(false);
        }
      } else {
        setDataLoading(false);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      setDataLoading(false);
    }
  };

const onSubmit = async (data) => {
  setLoading(true);
  try {
    const formData = new FormData();
    
    // Define allowed fields that can be updated
    const allowedFields = [
      'name',
      'phone_number', 
      'date_of_birth',
      'father_name',
      'mother_name',
      'gender',
      'district_of_ITI',
      'name_of_your_ITI',
      'name_of_trade',
      'batch_of_trade',
      'name_of_industry_partner',
      'isInformationCorrect',
      'course_affiliation_',
    ];
    
    // Only append allowed fields
    Object.keys(data).forEach(key => {
      if (allowedFields.includes(key) && data[key] !== null && data[key] !== undefined && data[key] !== "") {
        formData.append(key, data[key]);
      }
    });
    
    // Ensure registration_id is added as a string
    formData.append('registration_id', String(registrationId));


    const response = await putMethodApiCall("/v1/api/trainee/update-profile", formData);

    if (response.status === 200 && response?.data?.success) {
      toast.success("Profile updated successfully!");
      
      const updatedUser = {
        ...currentUser,
        trainee: response.data.data
      };
      localStorage.setItem("loginedUser", JSON.stringify(updatedUser));
      navigate("/Trainee-details");
    } else {
      console.error("Update profile failed:", response?.data?.error);
    }
  } catch (error) {
    console.error("Update profile error:", error);
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="w-full">
      <div className="relative h-full flex justify-center items-center py-3 container mx-auto">
        {dataLoading ? (
          <div className=" px-8 py-6 mt-4 text-left bg-white rounded-md shadow-sm">
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-gray-600">Loading profile data...</p>
              </div>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="w-full px-8 py-6 mt-4 text-left bg-white rounded-md shadow-sm"
          >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-[20px] font-semibold text-primary">
              Update Profile
            </h2>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-primary hover:text-primary/80 focus:outline-none"
              aria-label="Go back"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <span>Back</span>
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-1">
            {/* Registration ID - Read Only */}
            <div className="w-full flex flex-col gap-2">
              <label className="font-[400] text-xs text-gray-600">
                Registration Number
              </label>
              <input
                disabled
                {...register("registration_id")}
                className="border rounded-md px-3 py-2 text-sm w-full outline-none disabled:bg-gray-50"
              />
            </div>

            {/* Name */}
            <div className="w-full flex flex-col gap-2">
              <label className="font-[400] text-xs text-gray-600">
                Full Name <span className="text-red-400">*</span>
              </label>
              <input
                {...register("name", {
                  required: "Full name is required",
                })}
                placeholder="Enter your full name"
                className="border rounded-md px-3 py-2 text-sm w-full outline-none"
              />
              {errors.name && (
                <span className="text-red-500 text-xs">{errors.name.message}</span>
              )}
            </div>

            {/* Phone Number */}
            <div className="w-full flex flex-col gap-2">
              <label className="font-[400] text-xs text-gray-600">
                Phone Number <span className="text-red-400">*</span>
              </label>
              <input
                {...register("phone_number", {
                  required: "Phone number is required",
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: "Phone number must be exactly 10 digits",
                  },
                })}
                placeholder="Enter phone number"
                className="border rounded-md px-3 py-2 text-sm w-full outline-none"
                type="tel"
              />
              {errors.phone_number && (
                <span className="text-red-500 text-xs">{errors.phone_number.message}</span>
              )}
            </div>

            {/* Gender */}
            <div className="w-full flex flex-col gap-2">
              <label className="font-[400] text-xs text-gray-600">
                Gender <span className="text-red-400">*</span>
              </label>
              <select
                {...register("gender", {
                  required: "Gender is required",
                })}
                className="border rounded-md px-3 py-2 text-sm w-full outline-none"
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Others">Others</option>
              </select>
              {errors.gender && (
                <span className="text-red-500 text-xs">{errors.gender.message}</span>
              )}
            </div>

            {/* Date of Birth */}
            <div className="w-full flex flex-col gap-2">
              <label className="font-[400] text-xs text-gray-600">
                Date of Birth <span className="text-red-400">*</span>
              </label>
              <input
                {...register("date_of_birth", {
                  required: "Date of birth is required",
                })}
                max={new Date(
                  new Date().getFullYear() - 14,
                  new Date().getMonth(),
                  new Date().getDate()
                )
                  .toISOString()
                  .split("T")[0]}
                className="border rounded-md px-3 py-2 text-sm w-full outline-none"
                type="date"
              />
              {errors.date_of_birth && (
                <span className="text-red-500 text-xs">{errors.date_of_birth.message}</span>
              )}
            </div>

            {/* Father's Name */}
            <div className="w-full flex flex-col gap-2">
              <label className="font-[400] text-xs text-gray-600">
                Father&apos;s Name <span className="text-red-400">*</span>
              </label>
              <input
                {...register("father_name", {
                  required: "Father's name is required",
                })}
                placeholder="Enter father's name"
                className="border rounded-md px-3 py-2 text-sm w-full outline-none"
              />
              {errors.father_name && (
                <span className="text-red-500 text-xs">{errors.father_name.message}</span>
              )}
            </div>

            {/* Mother's Name */}
            <div className="w-full flex flex-col gap-2">
              <label className="font-[400] text-xs text-gray-600">
                Mother&apos;s Name <span className="text-red-400">*</span>
              </label>
              <input
                {...register("mother_name", {
                  required: "Mother's name is required",
                })}
                placeholder="Enter mother's name"
                className="border rounded-md px-3 py-2 text-sm w-full outline-none"
              />
              {errors.mother_name && (
                <span className="text-red-500 text-xs">{errors.mother_name.message}</span>
              )}
            </div>

            {/* Select Batch */}
            <div className="w-full flex flex-col gap-2">
              <label className="font-[400] text-xs text-gray-600">
                Select Batch <span className="text-red-400">*</span>
              </label>
              <select
                {...register("batch_of_trade", {
                  required: "Batch is required",
                })}
                className="border rounded-md px-3 py-2 text-sm w-full outline-none"
              >
                <option value="">Select Batch</option>
                <option value="2020-2022">2023 - 2024</option>
                <option value="2021-2022">2023 - 2025</option>
                <option value="2025-2026">2025 - 2026</option>
                <option value="2025-2027">2025 - 2027</option>
              </select>
              {errors.batch_of_trade && (
                <span className="text-red-500 text-xs">{errors.batch_of_trade.message}</span>
              )}
            </div>

            {/* Select District */}
            <div className="w-full flex flex-col gap-2">
              <label className="font-[400] text-xs text-gray-600">
                Select District <span className="text-red-400">*</span>
              </label>
              <input
                {...register("district_of_ITI", {
                  required: "District is required",
                })}
                placeholder="Enter district"
                className="border rounded-md px-3 py-2 text-sm w-full outline-none"
              />
              {errors.district_of_ITI && (
                <span className="text-red-500 text-xs">{errors.district_of_ITI.message}</span>
              )}
            </div>

            {/* Select ITI */}
            <div className="w-full flex flex-col gap-2">
              <label className="font-[400] text-xs text-gray-600">
                Select ITI <span className="text-red-400">*</span>
              </label>
              <input
                {...register("name_of_your_ITI", {
                  required: "ITI name is required",
                })}
                placeholder="Enter ITI name"
                className="border rounded-md px-3 py-2 text-sm w-full outline-none"
              />
              {errors.name_of_your_ITI && (
                <span className="text-red-500 text-xs">{errors.name_of_your_ITI.message}</span>
              )}
            </div>

            {/* Select Trade */}
            <div className="w-full flex flex-col gap-2">
              <label className="font-[400] text-xs text-gray-600">
                Select Trade <span className="text-red-400">*</span>
              </label>
              <input
                {...register("name_of_trade", {
                  required: "Trade is required",
                })}
                placeholder="Enter trade name"
                className="border rounded-md px-3 py-2 text-sm w-full outline-none"
              />
              {errors.name_of_trade && (
                <span className="text-red-500 text-xs">{errors.name_of_trade.message}</span>
              )}
            </div>

            {/* Select Industry */}
            <div className="w-full flex flex-col gap-2">
              <label className="font-[400] text-xs text-gray-600">
                Select Industry <span className="text-red-400">*</span>
              </label>
              <input
                {...register("name_of_industry_partner", {
                  required: "Industry is required",
                })}
                placeholder="Enter industry name"
                className="border rounded-md px-3 py-2 text-sm w-full outline-none"
              />
              {errors.name_of_industry_partner && (
                <span className="text-red-500 text-xs">{errors.name_of_industry_partner.message}</span>
              )}
            </div>

            {/* Course Affiliation */}
            <div className="w-full flex flex-col gap-2">
              <label className="font-[400] text-xs text-gray-600">
                Course Affiliation
              </label>
              <div className="flex mt-1.5 text-gray-500 gap-2 custom-radio">
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    value="NCVT"
                    {...register("course_affiliation_")}
                  />
                  NCVT
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    value="SCVT"
                    {...register("course_affiliation_")}
                  />
                  SCVT
                </label>
              </div>
            </div>

            {/* Is this information correct? */}
            <div className="w-full flex flex-col gap-2">
              <label className="font-[400] text-xs text-gray-600">
                Is this information correct? <span className="text-red-400">*</span>
              </label>
              <div className="flex mt-1.5 text-gray-500 gap-2 custom-radio">
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    value="Yes"
                    {...register("isInformationCorrect", {
                      required: "Please confirm if information is correct",
                    })}
                  />
                  Yes
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    value="No"
                    {...register("isInformationCorrect", {
                      required: "Please confirm if information is correct",
                    })}
                  />
                  No
                </label>
              </div>
              {errors.isInformationCorrect && (
                <span className="text-red-500 text-xs">{errors.isInformationCorrect.message}</span>
              )}
            </div>
            
          </div>

          {/* Submit Button */}
          { <div className="w-full mt-6 flex justify-center items-center space-x-2">
            <button
              type="submit"
              disabled={loading}
              className="w-auto px-8 py-2 bg-primary hover:bg-primary/90 text-white rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Updating..." : "Update Profile"}
            </button>
          </div> }
        </form>
        )}
      </div>
    </div>
  );
};

export default UpdateProfileForm; 